import { Component, createSignal, Show, For } from "solid-js";
import { createQuery, useQueryClient } from "@tanstack/solid-query";
import { PanelLayout, type SubTab, type PanelStatus } from "@/shared/panel-base";
import { DataTable, type Column } from "@/shared/data-table";
import { useConfirm } from "@/shared/confirm-dialog";
import { useMe } from "@/shared/use-me";
import { mapError } from "@/shared/error-mapping";
import { registerPanel } from "@/panel-registry";
import { apiFetch } from "@/shared/api-fetch";
import { Drawer } from "@/shared/drawer";
import "./user-admin.css";

interface UserRow {
  id: string;
  provider: string;
  email: string | null;
  name: string;
  roles: string[];
  disabledAt: string | null;
  lastLoginAt: string | null;
  mustChangePassword: boolean;
  createdAt: string;
}

const UsersPanel: Component = () => {
  const confirm = useConfirm();
  const qc = useQueryClient();
  const me = useMe();
  const [search, setSearch] = createSignal("");
  const [createOpen, setCreateOpen] = createSignal(false);
  const [actionStatus, setActionStatus] = createSignal<PanelStatus | null>(null);
  // Drawer affichant le mot de passe temporaire généré.
  const [secret, setSecret] = createSignal<{ user: UserRow; password: string } | null>(null);
  // Sélection multiple (bulk).
  const [selected, setSelected] = createSignal<Record<string, boolean>>({});
  const [bulkProgress, setBulkProgress] = createSignal<{ done: number; total: number } | null>(null);

  const isAdmin = (): boolean => !!me.data?.roles?.includes("admin");

  const usersQ = createQuery(() => ({
    queryKey: ["auth-admin-users", search()],
    queryFn: async () => {
      const r = await apiFetch(`/auth/admin/users?search=${encodeURIComponent(search())}`, {
        credentials: "include",
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json() as Promise<{ users: UserRow[] }>;
    },
    refetchInterval: 30_000,
  }));

  const rows = (): UserRow[] => usersQ.data?.users || [];
  const selectedRows = (): UserRow[] => rows().filter((u) => selected()[u.id]);

  function setRowSelected(id: string, on: boolean): void {
    setSelected((prev) => {
      const next = { ...prev };
      if (on) next[id] = true;
      else delete next[id];
      return next;
    });
  }

  function clearSelection(): void {
    setSelected({});
  }

  function toggleAll(on: boolean): void {
    if (!on) {
      clearSelection();
      return;
    }
    const next: Record<string, boolean> = {};
    for (const u of rows()) next[u.id] = true;
    setSelected(next);
  }

  /** Met à jour un champ texte simple (name/email) via PATCH. */
  async function patchField(u: UserRow, field: "name" | "email", value: string): Promise<void> {
    const trimmed = value.trim();
    const current = field === "name" ? u.name : u.email || "";
    if (trimmed === current) return;
    if (!trimmed) {
      setActionStatus({ text: `${field === "name" ? "Nom" : "Email"} requis.`, level: "danger" });
      return;
    }
    setActionStatus({ text: "Mise à jour...", level: "info" });
    const r = await apiFetch(`/auth/admin/users/${u.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ [field]: trimmed }),
    });
    if (!r.ok) {
      const j = (await r.json().catch(() => ({}))) as { error?: string };
      setActionStatus({ text: `Erreur: ${j.error ? mapError(j.error) : `HTTP ${r.status}`}`, level: "danger" });
      return;
    }
    setActionStatus({ text: "Champ mis à jour.", level: "info" });
    qc.invalidateQueries({ queryKey: ["auth-admin-users"] });
  }

  async function resetPassword(u: UserRow): Promise<void> {
    const generate = await confirm.confirm({
      title: `Reset mot de passe ${u.name}`,
      level: "danger",
      body: "Générer un mot de passe temporaire ?",
      entityName: u.email || u.name,
    });
    if (!generate) return;
    setActionStatus({ text: `Génération du mot de passe pour ${u.name}...`, level: "info" });
    const r = await apiFetch(`/auth/admin/users/${u.id}/reset-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ generate: true }),
    });
    if (!r.ok) {
      setActionStatus({ text: `Erreur: HTTP ${r.status}`, level: "danger" });
      return;
    }
    const { password } = (await r.json()) as { password?: string };
    if (password) {
      setSecret({ user: u, password });
      setActionStatus({ text: "Mot de passe temporaire généré.", level: "info" });
    } else {
      setActionStatus({ text: "Mot de passe réinitialisé.", level: "info" });
    }
    qc.invalidateQueries({ queryKey: ["auth-admin-users"] });
  }

  /** Applique disabled=value sur un utilisateur ; retourne true si succès. */
  async function applyDisable(u: UserRow, disabled: boolean): Promise<boolean> {
    const r = await apiFetch(`/auth/admin/users/${u.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ disabled }),
    });
    return r.ok;
  }

  async function toggleDisable(u: UserRow): Promise<void> {
    const willDisable = !u.disabledAt;
    const ok = await confirm.confirm({
      title: willDisable ? `Désactiver ${u.name}` : `Réactiver ${u.name}`,
      level: willDisable ? "danger" : "warn",
      body: "Confirmer ?",
      entityName: willDisable ? u.email || u.name : undefined,
    });
    if (!ok) return;
    setActionStatus({ text: `${willDisable ? "Désactivation" : "Réactivation"} de ${u.name}...`, level: "info" });
    const success = await applyDisable(u, willDisable);
    if (!success) {
      setActionStatus({ text: "Erreur lors de la mise à jour.", level: "danger" });
      return;
    }
    setActionStatus({ text: `${u.name} ${willDisable ? "désactivé" : "réactivé"}.`, level: "info" });
    qc.invalidateQueries({ queryKey: ["auth-admin-users"] });
  }

  async function deleteUser(u: UserRow): Promise<void> {
    const ok = await confirm.confirm({
      title: `Supprimer ${u.name}`,
      level: "danger",
      body: "Suppression soft (compte désactivé).",
      entityName: u.email || u.name,
    });
    if (!ok) return;
    setActionStatus({ text: `Suppression de ${u.name}...`, level: "info" });
    const r = await apiFetch(`/auth/admin/users/${u.id}`, {
      method: "DELETE",
      credentials: "include",
    });
    if (!r.ok) {
      setActionStatus({ text: `Erreur: HTTP ${r.status}`, level: "danger" });
      return;
    }
    setActionStatus({ text: `${u.name} supprimé.`, level: "info" });
    qc.invalidateQueries({ queryKey: ["auth-admin-users"] });
  }

  /** Action de masse : active ou désactive tous les utilisateurs sélectionnés. */
  async function bulkDisable(disabled: boolean): Promise<void> {
    const targets = selectedRows().filter((u) => (disabled ? !u.disabledAt : !!u.disabledAt));
    if (targets.length === 0) {
      setActionStatus({ text: "Aucun utilisateur concerné par cette action.", level: "warn" });
      return;
    }
    const label = disabled ? "Désactiver" : "Réactiver";
    const ok = await confirm.confirm({
      title: `${label} ${targets.length} utilisateur${targets.length > 1 ? "s" : ""}`,
      level: disabled ? "danger" : "warn",
      body: `Confirmer l'action "${label}" en masse sur ${targets.length} compte${targets.length > 1 ? "s" : ""} ?`,
      confirmLabel: label,
    });
    if (!ok) return;
    setBulkProgress({ done: 0, total: targets.length });
    let failures = 0;
    for (let i = 0; i < targets.length; i++) {
      const success = await applyDisable(targets[i]!, disabled);
      if (!success) failures++;
      setBulkProgress({ done: i + 1, total: targets.length });
    }
    setBulkProgress(null);
    clearSelection();
    qc.invalidateQueries({ queryKey: ["auth-admin-users"] });
    setActionStatus(
      failures
        ? { text: `${label} terminé — ${failures} échec(s) sur ${targets.length}.`, level: "danger" }
        : { text: `${label} : ${targets.length} compte(s) traité(s).`, level: "info" },
    );
  }

  /** Cellule éditable inline (double-clic → champ, sauvegarde onBlur). */
  function InlineCell(props: { user: UserRow; field: "name" | "email" }) {
    const [editing, setEditing] = createSignal(false);
    const value = (): string => (props.field === "name" ? props.user.name : props.user.email || "");
    return (
      <Show
        when={editing()}
        fallback={
          <span
            class={`ua-inline ${value() ? "" : "ua-inline--empty"}`}
            title="Double-clic pour modifier"
            onDblClick={() => setEditing(true)}
          >
            {value() || "—"}
          </span>
        }
      >
        <input
          class="ua-inline__input"
          type={props.field === "email" ? "email" : "text"}
          value={value()}
          autofocus
          onBlur={(e) => {
            setEditing(false);
            void patchField(props.user, props.field, e.currentTarget.value);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.currentTarget.blur();
            else if (e.key === "Escape") {
              e.currentTarget.value = value();
              setEditing(false);
            }
          }}
        />
      </Show>
    );
  }

  const allChecked = (): boolean => rows().length > 0 && rows().every((u) => selected()[u.id]);

  const columns: Column<UserRow>[] = [
    {
      id: "_select",
      header: () => (
        <span class="ua-check">
          <input
            type="checkbox"
            checked={allChecked()}
            title="Tout sélectionner"
            onChange={(e) => toggleAll(e.currentTarget.checked)}
          />
        </span>
      ),
      enableSorting: false,
      cell: (info) => {
        const u = info.row.original;
        return (
          <span class="ua-check">
            <input
              type="checkbox"
              checked={!!selected()[u.id]}
              onChange={(e) => setRowSelected(u.id, e.currentTarget.checked)}
            />
          </span>
        );
      },
    },
    { id: "email", header: "Email", cell: (info) => <InlineCell user={info.row.original} field="email" /> },
    { id: "name", header: "Nom", cell: (info) => <InlineCell user={info.row.original} field="name" /> },
    { accessorKey: "provider", header: "Provider" },
    { accessorFn: (r) => r.roles.join(", "), id: "roles", header: "Rôles" },
    {
      accessorFn: (r) => (r.disabledAt ? "désactivé" : "actif"),
      id: "status",
      header: "Statut",
    },
    {
      accessorFn: (r) => (r.lastLoginAt ? new Date(r.lastLoginAt).toLocaleString() : "—"),
      id: "last",
      header: "Dernière connexion",
    },
  ];

  const subtabs: SubTab[] = [
    {
      id: "list",
      label: "Comptes",
      render: () => (
        <>
          <div class="panel-toolbar panel-toolbar--filter">
            <input
              class="panel-filter-input"
              placeholder="Rechercher email/nom..."
              value={search()}
              onInput={(e) => setSearch(e.currentTarget.value)}
            />
            <button class="btn btn--xs btn--info" onClick={() => setCreateOpen(true)}>
              + Créer utilisateur
            </button>
          </div>
          <Show when={selectedRows().length > 0}>
            <div class="ua-bulkbar">
              <span class="ua-bulkbar__count">{selectedRows().length} sélectionné(s)</span>
              <button
                type="button"
                class="btn btn--xs btn--ghost"
                disabled={!!bulkProgress()}
                onClick={() => bulkDisable(false)}
              >
                Réactiver
              </button>
              <button
                type="button"
                class="btn btn--xs btn--warn"
                disabled={!!bulkProgress()}
                onClick={() => bulkDisable(true)}
              >
                Désactiver
              </button>
              <span class="ua-bulkbar__spacer" />
              <button type="button" class="btn btn--xs btn--ghost" onClick={clearSelection}>
                Effacer
              </button>
              <Show when={bulkProgress()}>
                {(p) => (
                  <>
                    <div class="ua-bulkbar__progress">
                      <div
                        class="ua-bulkbar__progress-fill"
                        style={{ width: `${(p().done / Math.max(1, p().total)) * 100}%` }}
                      />
                    </div>
                    <span class="ua-bulkbar__progress-label">
                      {p().done} / {p().total}
                    </span>
                  </>
                )}
              </Show>
            </div>
          </Show>
          <DataTable<UserRow>
            rows={rows()}
            columns={columns}
            rowActions={(u) => (
              <>
                <button class="btn btn--xs btn--ghost" onClick={() => resetPassword(u)}>
                  Reset MDP
                </button>
                <button
                  class={`btn btn--xs ${u.disabledAt ? "btn--ghost" : "btn--warn"}`}
                  onClick={() => toggleDisable(u)}
                >
                  {u.disabledAt ? "Réactiver" : "Désactiver"}
                </button>
                <button class="btn btn--xs btn--danger" onClick={() => deleteUser(u)}>
                  Supprimer
                </button>
              </>
            )}
            emptyMessage="Aucun utilisateur."
          />
          <Show when={createOpen()}>
            <CreateUserModal
              onClose={() => setCreateOpen(false)}
              onCreated={() => {
                setCreateOpen(false);
                qc.invalidateQueries({ queryKey: ["auth-admin-users"] });
              }}
            />
          </Show>
          <Drawer open={!!secret()} onClose={() => setSecret(null)} title="Mot de passe temporaire" width="440px">
            <Show when={secret()}>
              {(s) => (
                <div class="ua-form">
                  <UserProps
                    title="Compte concerné"
                    entries={[
                      ["Nom", s().user.name],
                      ["Email", s().user.email || "—"],
                      ["Rôles", s().user.roles.join(", ") || "—"],
                    ]}
                  />
                  <div class="ua-form__field">
                    <span class="ua-form__label">Mot de passe</span>
                    <div class="ua-secret">
                      <span class="ua-secret__value">{s().password}</span>
                      <button
                        type="button"
                        class="btn btn--xs btn--info"
                        onClick={() => {
                          void navigator.clipboard?.writeText(s().password);
                          setActionStatus({ text: "Mot de passe copié.", level: "info" });
                        }}
                      >
                        Copier
                      </button>
                    </div>
                    <span class="ua-form__hint">
                      À transmettre maintenant — il ne sera plus jamais affiché. Changement requis à la prochaine
                      connexion.
                    </span>
                  </div>
                  <div class="ua-form__actions">
                    <button type="button" class="btn btn--sm btn--ghost" onClick={() => setSecret(null)}>
                      Fermer
                    </button>
                  </div>
                </div>
              )}
            </Show>
          </Drawer>
        </>
      ),
    },
  ];

  const status = (): PanelStatus | undefined => {
    if (actionStatus()) return actionStatus() ?? undefined;
    if (usersQ.isLoading) return { text: "Chargement...", level: "info" };
    return undefined;
  };

  return (
    <Show
      when={isAdmin()}
      fallback={
        <PanelLayout
          title="Utilisateurs (admin)"
          subtabs={[
            {
              id: "denied",
              label: "Accès refusé",
              render: () => <div class="panel-data-empty">Accès admin requis pour gérer les utilisateurs.</div>,
            },
          ]}
          status={
            me.isLoading
              ? { text: "Vérification des droits...", level: "info" }
              : { text: "Permission administrateur requise.", level: "warn" }
          }
        />
      }
    >
      <PanelLayout title="Utilisateurs (admin)" subtabs={subtabs} status={status()} />
    </Show>
  );
};

/** Carte de propriétés en lecture seule, réutilisée dans les drawers. */
function UserProps(props: { title: string; entries: [string, string][] }) {
  return (
    <div class="ua-props">
      <h3 class="ua-props__title">{props.title}</h3>
      <div class="ua-props__grid">
        <For each={props.entries}>
          {([k, v]) => (
            <>
              <span class="ua-props__key">{k}</span>
              <span class="ua-props__val">{v}</span>
            </>
          )}
        </For>
      </div>
    </div>
  );
}

function CreateUserModal(props: { onClose: () => void; onCreated: () => void }) {
  const [email, setEmail] = createSignal("");
  const [name, setName] = createSignal("");
  const [password, setPassword] = createSignal("");
  const [roles, setRoles] = createSignal<string[]>(["support"]);
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);

  function toggleRole(r: string): void {
    const cur = roles();
    setRoles(cur.includes(r) ? cur.filter((x) => x !== r) : [...cur, r]);
  }

  async function submit(): Promise<void> {
    setError(null);
    if (!email() || !name() || password().length < 8 || roles().length === 0) {
      setError("Tous les champs requis (mot de passe ≥ 8 caractères, ≥ 1 rôle)");
      return;
    }
    setBusy(true);
    try {
      const r = await apiFetch("/auth/admin/users", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email: email(),
          name: name(),
          password: password(),
          roles: roles(),
        }),
      });
      if (!r.ok) {
        const j = (await r.json().catch(() => ({}))) as { error?: string };
        throw new Error(j.error ? mapError(j.error) : `Erreur HTTP ${r.status}`);
      }
      props.onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Drawer open onClose={props.onClose} title="Nouvel utilisateur" width="440px">
      <div class="ua-form">
        <div class="ua-form__field">
          <span class="ua-form__label">Email</span>
          <input
            class="panel-filter-input"
            placeholder="email@exemple.com"
            value={email()}
            onInput={(e) => setEmail(e.currentTarget.value)}
          />
        </div>
        <div class="ua-form__field">
          <span class="ua-form__label">Nom affiché</span>
          <input
            class="panel-filter-input"
            placeholder="Nom affiché"
            value={name()}
            onInput={(e) => setName(e.currentTarget.value)}
          />
        </div>
        <div class="ua-form__field">
          <span class="ua-form__label">Mot de passe</span>
          <input
            class="panel-filter-input"
            placeholder="Mot de passe (≥ 8 car.)"
            type="password"
            value={password()}
            onInput={(e) => setPassword(e.currentTarget.value)}
          />
        </div>
        <div class="ua-form__field">
          <span class="ua-form__label">Rôles</span>
          <div style={{ display: "flex", gap: "12px", color: "var(--text-3)" }}>
            <For each={["admin", "support", "auditor"]}>
              {(r) => (
                <label style={{ display: "flex", "align-items": "center", gap: "6px", "font-size": "12.5px" }}>
                  <input type="checkbox" checked={roles().includes(r)} onChange={() => toggleRole(r)} />
                  {r}
                </label>
              )}
            </For>
          </div>
        </div>
        <Show when={error()}>
          <div class="ua-form__error">{error()}</div>
        </Show>
        <div class="ua-form__actions">
          <button class="btn btn--sm btn--ghost" onClick={() => props.onClose()}>
            Annuler
          </button>
          <button class="btn btn--sm btn--info" onClick={submit} disabled={busy()}>
            {busy() ? "Création..." : "Créer"}
          </button>
        </div>
      </div>
    </Drawer>
  );
}

registerPanel("users", UsersPanel);
export default UsersPanel;
